import type { Budget, CostsState } from '@orgus/protocol';
import { CircleAlert, Download, Flame, Info, Plus, SquareTerminal } from '@orgus/ui/icons';
import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { BUTTON_SIZE, BUTTON_VARIANT, Banner, Button, RelativeTime, SectionHeader, Skeleton, SkeletonRows, Tooltip, cn, formatMonth, plural } from '@orgus/ui';
import { PageActions } from '../../components/shell/TopBar.tsx';
import { useOrgus } from '../../lib/store.ts';
import { AccountBreakdown, ModelMix, ProjectBreakdown } from './Breakdown.tsx';
import { mixedBilling } from './accounts.tsx';
import { BudgetDialog } from './BudgetDialog.tsx';
import { BudgetList } from './BudgetList.tsx';
import { formatTokensPerMin } from './model.ts';
import { dayText } from '../finance/model.ts';
import { SpendHero } from './SpendHero.tsx';
import { TopSessions } from './TopSessions.tsx';
import { SpendHistory } from '../history/PageHistory.tsx';
import { exportUrl } from './useCosts.ts';

/**
 * The labelled caveat on every figure, one quiet line: the basis in a word, the price date,
 * and the rest of the caveat on hover or focus.
 */
export function CostBasis({ costs }: { costs: CostsState }) {
  const notional = costs.billing === 'subscription';
  const mixed = mixedBilling(costs);
  const word = mixed ? 'Notional and list cost' : notional ? 'Notional cost' : 'API list cost';
  const why = mixed
    ? 'Notional for subscriptions, list price for usage billing; an invoice can differ'
    : notional
      ? 'What the tokens would cost at API list prices; a subscription has no per-token bill'
      : 'API list prices times the tokens used; an invoice can differ';
  const version = ISO_DAY.test(costs.pricing.version) ? dayText(costs.pricing.version) : costs.pricing.version;
  return (
    <p className="flex min-w-0 items-center gap-2 text-caption text-fg-subtle">
      <Info className="size-3.5 shrink-0" aria-hidden />
      <span className="flex min-w-0 items-center">
        <Tooltip content={`${why}. Prices as of ${version}, USD`}>
          <span tabIndex={0} className="shrink-0 rounded-control underline decoration-dotted underline-offset-2">
            {word}
          </span>
        </Tooltip>
        <span className="hidden min-w-0 truncate whitespace-pre md:inline">, USD at {version} prices</span>
      </span>
      <Link to="/settings/spend" className="ml-auto shrink-0 text-fg-muted underline-offset-2 hover:text-fg hover:underline">
        Change basis
      </Link>
    </p>
  );
}

const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/;

export function SpendSkeleton() {
  return (
    <div className="flex flex-col gap-6" role="status" aria-label="Loading spend">
      <div className="grid gap-6 xl:grid-cols-3">
        <div className="flex flex-col gap-6 rounded-card border border-border bg-card p-4 xl:col-span-2">
          <Skeleton className="h-4 w-40" />
          <div className="grid grid-cols-2 gap-8 lg:grid-cols-4">
            {Array.from({ length: 4 }, (_, i) => (
              <div key={i} className="flex flex-col gap-2">
                <Skeleton className="h-3 w-20" />
                <Skeleton className="h-8 w-24" />
              </div>
            ))}
          </div>
          <Skeleton className="h-56" />
        </div>
        <div className="rounded-card border border-border bg-card">
          <SkeletonRows rows={4} />
        </div>
      </div>
    </div>
  );
}

/** Spend this month against budget, the forecast, where it went, and the budgets themselves. */
export function SpendView({ costs }: { costs: CostsState }) {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [dialog, setDialog] = useState<{ open: boolean; budget: Budget | null }>({ open: false, budget: null });
  const live = useOrgus((s) => s.connection === 'live' || s.mode === 'mock');
  const mock = useOrgus((s) => s.mode === 'mock');

  const orgMonth = costs.budgets.find((b) => b.scope.kind === 'org' && b.period === 'month');
  const orgDay = costs.budgets.find((b) => b.scope.kind === 'org' && b.period === 'day');
  const orgStatus = orgMonth ? costs.budgetStatus.find((s) => s.budgetId === orgMonth.id) : undefined;
  const activeRunaways = costs.runaways.filter((r) => r.active);
  const month = formatMonth(costs.monthStart);
  const partialSessions = costs.sessions.filter((s) => s.partial).length;

  return (
    <div className="flex flex-col gap-6">
      <PageActions>
        <Tooltip content={mock ? 'Export needs a live hub; demo data has no ledger' : `Every usage row of ${month} as CSV, for chargeback or your accountant`}>
          {mock ? (
            <span tabIndex={0} className="hidden sm:inline-flex">
              <Button size="sm" variant="ghost" icon={<Download />} disabled>
                Export CSV
              </Button>
            </span>
          ) : (
            <a href={exportUrl()} download className={cn(BUTTON_VARIANT.ghost, BUTTON_SIZE.sm, 'hidden items-center font-medium sm:inline-flex')}>
              <Download aria-hidden />
              Export CSV
            </a>
          )}
        </Tooltip>
        <Button size="sm" variant="primary" icon={<Plus />} onClick={() => setDialog({ open: true, budget: null })}>
          Add budget
        </Button>
      </PageActions>

      {activeRunaways.map((r) => (
        <Banner
          key={r.sessionId}
          tone="attention"
          icon={<Flame />}
          title={`A session is burning ${Math.max(1, Math.round(r.rate / Math.max(1, r.baseline)))}x its usual rate`}
          actions={
            <Button size="sm" variant="secondary" onClick={() => navigate(`/sessions?session=${encodeURIComponent(r.sessionId)}`)}>
              Open session
            </Button>
          }
        >
          {/* The same live rate as the session's Burn now in Top sessions. */}
          <span className="line-clamp-1 text-caption text-fg-muted">
            {r.title || 'Untitled session'}, {formatTokensPerMin(r.rate)} since <RelativeTime ts={r.at} />
          </span>
        </Banner>
      ))}

      {costs.unpricedModels.length > 0 && (
        <Banner tone="attention" icon={<CircleAlert />} title={`No price for ${costs.unpricedModels.join(', ')}`}>
          Those tokens count as $0. Update Orgus to get its newer price list.
        </Banner>
      )}

      {/* Hero and Budgets share a row and end level; the cost basis line sits under the hero. */}
      <div className="grid gap-6 xl:grid-cols-3 xl:gap-y-2">
        <div className="flex min-w-0 flex-col xl:col-span-2">
          <SpendHero
            className="flex-1"
            costs={costs}
            orgMonth={orgMonth}
            orgStatus={orgStatus}
            dailyLimit={orgDay?.limit ?? null}
            partial={partialSessions ? `${plural(partialSessions, 'session')} still being read, so recent days may undercount.` : undefined}
            actions={
              <Tooltip content={live ? 'Updates live as sessions use tokens' : 'Not live: the hub connection dropped'}>
                <span className="hidden text-caption text-fg-subtle sm:inline" tabIndex={0}>
                  {live ? 'Live' : 'Paused'}, <RelativeTime ts={costs.updatedAt} />
                </span>
              </Tooltip>
            }
          />
        </div>
        <div className="-mt-4 xl:col-span-2 xl:row-start-2 xl:mt-0">
          <CostBasis costs={costs} />
        </div>
        <BudgetList
          className="xl:col-start-3 xl:row-start-1"
          budgets={costs.budgets}
          statuses={costs.budgetStatus}
          highlight={params.get('budget')}
          onAdd={() => setDialog({ open: true, budget: null })}
          onEdit={(b) => setDialog({ open: true, budget: b })}
        />
      </div>

      <SpendHistory />

      {/* Cards in a row stretch to one height, so the grid never looks ragged. */}
      <div className={cn('grid gap-6 lg:grid-cols-2', costs.byAccount && 'xl:grid-cols-3')}>
        <ProjectBreakdown buckets={costs.byProject} className="h-full" />
        {costs.byAccount && <AccountBreakdown buckets={costs.byAccount} costs={costs} className="h-full lg:order-last xl:order-none" />}
        <ModelMix buckets={costs.byModel} className="h-full" />
      </div>

      <section aria-label="Top sessions" className="flex flex-col gap-3">
        <SectionHeader title="Top sessions" count={costs.sessions.length || undefined} meta={<span className="hidden sm:inline">This month, most expensive first</span>} icon={<SquareTerminal />} />
        <TopSessions costs={costs} />
      </section>

      <BudgetDialog open={dialog.open} onOpenChange={(open) => setDialog((d) => ({ ...d, open }))} budget={dialog.budget} costs={costs} />
    </div>
  );
}
